import React, { useState } from 'react';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, Alert } from '@mui/material';
import api from '../../util/axiosInstance';
import { fetchAgreementPdf, saveAgreementPdf, PdfReader } from './DocumentAgreementModal';

export default function OrderAgreementProofs({ serviceType, cartItemId, eventId, providerId, label = 'Order agreements' }) {
  const [open, setOpen] = useState(false), [orders, setOrders] = useState([]);
  const [viewer, setViewer] = useState(null);
  const [page, setPage] = useState(1), [more, setMore] = useState(true), [busy, setBusy] = useState(false), [error, setError] = useState('');
  const load = async (reset = false) => {
    setBusy(true); setError('');
    try {
      if (serviceType && cartItemId) {
        const { data } = await api.get(`/document-agreements/orders/${serviceType}/${cartItemId}`);
        setOrders([{ serviceType, cartItemId, documents: data.documents }]); setMore(false);
        return;
      }
      const { data } = await api.get('/document-agreements/bookings', { params: { page: reset ? 1 : page,
        ...(serviceType ? { serviceType } : {}), ...(eventId ? { eventId } : {}), ...(providerId ? { providerId } : {}) } });
      setOrders(prev => reset ? data.orders : [...prev, ...data.orders]); setPage(data.page + 1); setMore(data.hasMore);
    } catch (_) { setError('Unable to load order agreements. Please try again.'); }
    finally { setBusy(false); }
  };
  const view = async (doc, signed) => {
    setBusy(true); setError('');
    try { setViewer({ bytes: await fetchAgreementPdf(signed ? doc.signedContractUrl : doc.originalUrl), name: signed ? `signed-${doc.fileName}` : doc.fileName }); }
    catch (_) { setError('Unable to open this document.'); }
    finally { setBusy(false); }
  };
  return <>
    <Button disabled={busy} onClick={() => { setOrders([]); setViewer(null); setPage(1); setMore(true); setOpen(true); load(true); }}>{label}</Button>
    <Dialog open={open} onClose={() => { if (!busy) setOpen(false); }} fullWidth maxWidth="md">
      <DialogTitle>Order document agreements</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error">{error}</Alert>}
        {busy && <p role="status">Loading documents…</p>}
        {!busy && !orders.length && !error && <p>No completed order agreements yet.</p>}
        {orders.map(order => <section className="agreement-vendor" key={`${order.serviceType}:${order.cartItemId}`}>
          <h3>{order.serviceType} - Booking #{order.cartItemId}</h3>
          {!order.documents.length && <p>No completed agreements for this booking yet.</p>}
          {order.documents.map(doc => <div key={doc.id} className="agreement-row">
            <span className="agreement-complete">&#10003;</span>
            <div className="agreement-file"><strong>{doc.fileName}</strong><span>{doc.status} - {doc.agreedAt && new Date(doc.agreedAt).toLocaleString()}{doc.signerName && ` - ${doc.signerName}`}</span></div>
            <Button disabled={busy} onClick={() => view(doc, false)}>View original</Button>
            {doc.signedContractUrl && <Button disabled={busy} onClick={() => view(doc, true)}>View signed PDF</Button>}
          </div>)}
        </section>)}
        {(more || error) && <Button disabled={busy} onClick={() => load()}>{busy ? 'Loading...' : error ? 'Retry' : 'Load more'}</Button>}
      </DialogContent>
      <DialogActions><Button disabled={busy} onClick={() => setOpen(false)}>Close</Button></DialogActions>
    </Dialog>
    <Dialog open={!!viewer} onClose={() => setViewer(null)} fullWidth maxWidth="md">
      <DialogTitle>{viewer?.name}</DialogTitle>
      <DialogContent>{viewer && <PdfReader bytes={viewer.bytes} />}</DialogContent>
      <DialogActions>
        <Button onClick={() => setViewer(null)}>Close</Button>
        <Button disabled={!viewer} onClick={() => saveAgreementPdf(viewer.bytes, viewer.name)}>Download PDF</Button>
      </DialogActions>
    </Dialog>
  </>;
}
